import { type V3 } from "@project-rouge/rg-core";
import { type ThreeEvent } from "@react-three/fiber";

import { useBoolean } from "src/hooks/useBoolean";
import { useEvent } from "src/hooks/useEvent";
import { Cluster } from "src/components/Cluster";
import { PolygonEdges } from "./PolygonEdges";
import { PolygonFace } from "./PolygonFace";
import { PolygonVertexes } from "./PolygonVertexes";
import { observer } from "mobx-react-lite";
import { ZoneType, type Zone } from "src/types/Zone";
import { useState } from "react";
import { useWindowEvent } from "src/hooks/useWindowEvent";
import { GetZoneStyle, ZONE_POLYGON_STYLE } from "./ZONE_POLYGON_STYLE";
import type { StyleSheet } from "./ZONE_POLYGON_STYLE";
import { Tooltip } from "./Tooltip";

function GetStyle(props: { styleSheet: StyleSheet; hover?: boolean; select?: boolean }) {
  if (props.hover && props.select) return props.styleSheet.SELECT_HOVER;
  if (props.hover) return props.styleSheet.HOVER;
  if (props.select) return props.styleSheet.SELECT;
  return props.styleSheet.NONE;
}

export enum ZonePolygonType {
  BUILDABLE = "BUILDABLE",
  EXCLUSION = "EXCLUSION",
}

export const ZonePolygon = observer(
  (props: {
    zone: Zone;
    points: V3[];
    type: ZonePolygonType;
    showEdges?: boolean;
    showVertexes?: boolean;
    hoverEnabled?: boolean;
    selectEnabled?: boolean;
    selected?: boolean;
    depthTest?: boolean;
    showOffsetEdges?: boolean;
    offsets: number[] | null;
    enableWarnings?: boolean;
    onSelect?: (event: ThreeEvent<PointerEvent>) => void;
    onHoverChange?: (isHovered: boolean, event: ThreeEvent<PointerEvent>) => void;
  }) => {
    const vertexRadius = 6.5 / 2;
    const vertexBorderWidth = 1.5;
    const showEdges = props.showEdges ?? true;
    const showVertexes = props.showVertexes ?? true;
    const isHover = useBoolean();

    const style = GetStyle({
      styleSheet: GetZoneStyle(props.zone, props.enableWarnings ?? false),
      hover: props.hoverEnabled && isHover.isOn,
      select: props.selectEnabled && props.selected,
    });

    const styleOffset = GetStyle({
      styleSheet: ZONE_POLYGON_STYLE.BUILDIABLE_OFFSET,
      hover: props.hoverEnabled && isHover.isOn,
      select: props.selectEnabled && props.selected,
    });

    const handleHoverEnter = useEvent((evt: ThreeEvent<PointerEvent>) => {
      isHover.on();
      props.onHoverChange?.(true, evt);
    });
    const handleHoverLeave = useEvent((evt: ThreeEvent<PointerEvent>) => {
      isHover.off();
      props.onHoverChange?.(false, evt);
    });

    return (
      <group
        onPointerEnter={handleHoverEnter}
        onPointerLeave={handleHoverLeave}
        onPointerDown={props.onSelect}
      >
        {props.zone.polygons.innerPolygons.map((polygon, i) => (
          <PolygonFace
            key={PolygonToId(polygon, i)}
            points={polygon}
            fill={style.FILL_COLOR}
            opacity={style.FILL_ALPHA}
          />
        ))}

        {props.zone.polygons.outterPolygons.map((polygon, i) => (
          <PolygonFace
            key={PolygonToId(polygon, i)}
            fill={styleOffset.FILL_COLOR}
            opacity={styleOffset.FILL_ALPHA}
            points={polygon}
            holes={props.zone.polygons.outterPolygonsHoles}
          />
        ))}
        <Cluster visible={!!props.showOffsetEdges}>
          {props.zone.polygons.innerPolygons.map((offsetPolygon, i) => (
            <PolygonEdges
              key={PolygonToId(offsetPolygon, i)}
              points={offsetPolygon}
              lineWidthPx={styleOffset.BORDER_WIDTH}
              color={styleOffset.BORDER_COLOR}
              opacity={styleOffset.BORDER_ALPHA}
              dashSizePx={styleOffset.DASH_SIZE_PX}
              gapSizePx={styleOffset.GAP_SIZE_PX}
              dashed
              y={0}
              depthTest={true}
            />
          ))}
        </Cluster>
        <Cluster visible={showEdges}>
          <PolygonEdges
            points={props.points}
            lineWidthPx={style.BORDER_WIDTH}
            color={style.BORDER_COLOR}
            opacity={style.BORDER_ALPHA}
            depthTest={props.depthTest}
            offsetPx={showVertexes ? vertexBorderWidth + vertexRadius : 0}
          />
        </Cluster>
        <Cluster visible={showVertexes}>
          <PolygonVertexes
            points={props.points}
            radiusPx={vertexRadius}
            fillColor={0xffffff}
            borderWidthPx={vertexBorderWidth}
            borderColor={0x58588e}
          />
        </Cluster>
        <EmptyZoneTooltip zone={props.zone} enabled={!!props.enableWarnings && isHover.isOn} />
      </group>
    );
  }
);

const EmptyZoneTooltip = observer(function EmptyZoneTooltip(props: {
  zone: Zone;
  enabled: boolean;
}) {
  const [mouse, setMouse] = useState<null | { x: number; y: number }>(null);
  useWindowEvent("mousemove", (evt) => {
    setMouse({ x: evt.clientX, y: evt.clientY });
  });
  if (!props.enabled) return null;
  if (props.zone.zoneType !== ZoneType.buildable) return null;
  if (props.zone.hasBuilding) return null;
  if (!mouse) return null;

  return (
    <Tooltip.Mouse clientX={mouse.x} clientY={mouse.y}>
      <div className="flex-col justify-center bg-neutral-8 text-white py-[2px] px-2 rounded-sm shadow">
        <div className="flex flex-row items-center space-x-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="10"
            viewBox="0 0 12 10"
            fill="none"
          >
            <path
              d="M5.99994 0C6.47747 0 6.9225 0.241643 7.19502 0.645245L7.24752 0.728035L11.3048 7.73828C11.4281 7.95927 11.4952 8.20902 11.4997 8.46415C11.5043 8.71928 11.4463 8.97145 11.331 9.19703C11.2157 9.42261 11.047 9.61423 10.8407 9.75395C10.6343 9.89366 10.3971 9.9769 10.1512 9.99586L10.0537 10H1.94069C1.69453 9.99713 1.45308 9.92977 1.23889 9.80421C1.02469 9.67864 0.844729 9.49896 0.715802 9.28194C0.586876 9.06492 0.513186 8.81764 0.501612 8.56317C0.490037 8.30871 0.540956 8.05535 0.649615 7.82676L0.699118 7.73104L4.75437 0.725965C4.88421 0.504405 5.06703 0.321209 5.28521 0.194049C5.50338 0.066889 5.74954 5.70047e-05 5.99994 0ZM6.00494 6.89744L5.94144 6.90107C5.81991 6.91602 5.7079 6.97659 5.62663 7.07128C5.54536 7.16598 5.50047 7.28823 5.50047 7.41488C5.50047 7.54153 5.54536 7.66378 5.62663 7.75848C5.7079 7.85317 5.81991 7.91374 5.94144 7.9287L5.99994 7.93232L6.06345 7.9287C6.18498 7.91374 6.29699 7.85317 6.37826 7.75848C6.45953 7.66378 6.50442 7.54153 6.50442 7.41488C6.50442 7.28823 6.45953 7.16598 6.37826 7.07128C6.29699 6.97659 6.18498 6.91602 6.06345 6.90107L6.00494 6.89744ZM5.99994 3.27538C5.87747 3.2754 5.75926 3.32193 5.66774 3.40614C5.57621 3.49036 5.51774 3.60641 5.50341 3.73228L5.49991 3.79282V5.86257L5.50341 5.92311C5.51787 6.04887 5.5764 6.16478 5.66791 6.24888C5.75942 6.33298 5.87755 6.37943 5.99994 6.37943C6.12233 6.37943 6.24047 6.33298 6.33198 6.24888C6.42349 6.16478 6.48202 6.04887 6.49647 5.92311L6.49997 5.86257V3.79282L6.49647 3.73228C6.48214 3.60641 6.42367 3.49036 6.33215 3.40614C6.24063 3.32193 6.12242 3.2754 5.99994 3.27538Z"
              fill="#FAFAFA"
            />
          </svg>
          <span className="text-subtitle-2">No buildings in zone</span>
        </div>
        <span className="text-body-1">It may be too small.</span>
      </div>
    </Tooltip.Mouse>
  );
});

function PolygonToId(polygon: V3[], index: number) {
  return `${polygon.join()}-${index}`;
}
